import { useState } from 'react';
import { FileClock, Search } from 'lucide-react';
import { useFormat } from '@/hooks/useAccess';
import { useCustomersQuery, type Customer } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { SummaryCards } from '@/components/SummaryCards';
import { formatMoney } from '@/lib/format';
import {
  Badge, Card, EmptyState, ErrorState, Input, LoadingState, PageHeader, Pagination,
  Select, Table, Td, Th,
} from '@/components/ui';
import { CustomerDetailModal } from './CustomerDetailModal';

/**
 * Receivables: who owes money and how much room they have left.
 *
 * The statement itself lives in the customer modal, because a balance and its
 * explanation belong together. This screen is the way in — it answers "who owes
 * us" first, which is the question that actually gets asked.
 */
export function StatementsPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'owing' | 'overLimit' | 'all'>('owing');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useCustomersQuery({
    page,
    limit: 25,
    search: search || undefined,
    owingOnly: filter === 'owing' || undefined,
    overLimitOnly: filter === 'overLimit' || undefined,
  });

  return (
    <>
      <PageHeader
        title="Statements"
        description="What customers owe. Open one to see every movement behind the balance."
      />

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Customers', value: String(data.summary.count) },
            {
              label: 'Owed to us',
              value: formatMoney(String(data.summary.owed), format),
              hint: 'across these customers',
              tone: 'owed',
            },
            {
              label: 'Credit extended',
              value: formatMoney(String(data.summary.creditExtended), format),
              hint: 'total of their limits',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Name, code or phone"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-56">
            <Select
              label="Show"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as typeof filter);
                setPage(1);
              }}
            >
              <option value="owing">Owing money</option>
              <option value="overLimit">At or over their limit</option>
              <option value="all">Every customer</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={FileClock}
            title={filter === 'owing' ? 'Nobody owes anything' : 'Nothing matches'}
            description={
              filter === 'owing'
                ? 'Every customer account is settled.'
                : 'Try a different filter or search.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Phone</Th>
                  <Th numeric>Balance</Th>
                  <Th numeric>Credit limit</Th>
                  <Th numeric>Credit left</Th>
                  <Th>Terms</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((customer: Customer) => (
                  <tr key={customer.id} className="hover:bg-sunken">
                    <Td>
                      <button
                        type="button"
                        className="text-left font-medium hover:underline"
                        onClick={() => setOpenId(customer.id)}
                      >
                        {customer.name}
                      </button>
                      <p className="text-xs text-muted">{customer.code}</p>
                    </Td>
                    <Td className="text-secondary">{customer.phone ?? '—'}</Td>
                    <Td
                      numeric
                      className={Number(customer.balance) > 0 ? 'font-medium text-red-600' : 'text-secondary'}
                    >
                      {formatMoney(customer.balance, format)}
                    </Td>
                    <Td numeric className="text-secondary">
                      {formatMoney(customer.creditLimit, format)}
                    </Td>
                    <Td numeric className="text-secondary">
                      {customer.availableCredit === null
                        ? 'Cash only'
                        : formatMoney(customer.availableCredit, format)}
                    </Td>
                    <Td className="text-secondary">
                      {customer.paymentTermDays > 0 ? `${customer.paymentTermDays} days` : 'On demand'}
                    </Td>
                    <Td>
                      {customer.isOverLimit ? (
                        <Badge tone="danger">Over limit</Badge>
                      ) : Number(customer.balance) > 0 ? (
                        <Badge tone="warning">Owing</Badge>
                      ) : (
                        <Badge tone="success">Settled</Badge>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {openId && <CustomerDetailModal customerId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
